import { log } from 'log';
import { forEachObjIndexed, reduce } from 'ramda';
import { languageService } from 'js/services/language.service';

/**
 * @param params {Object}
 * @param origin {window.location | string}
 * @returns {string}
 */
export const addParamsToUrl = (params = {}, origin = location) => {
    let url;
    let baseURL = origin.href || origin;

    try {
        if (baseURL.startsWith?.('/')) {
            baseURL = `${location.origin}${baseURL}`;
        }

        url = new window.URL(baseURL, document.baseURI);
    } catch (err) {
        throw new Error(`The passed string (${baseURL}) is not a URL.`);
    }

    const { searchParams } = url;

    forEachObjIndexed((value = '', key) => searchParams.set(key, value), params);

    return url.toString();
};

export const getSearchWithoutParameters = (keysToRemove = [], search = location.search) => {
    const searchParams = new window.URLSearchParams(search);

    keysToRemove.forEach(key => searchParams.delete(key));

    return `?${searchParams.toString()}`;
};

/**
 * @param [str] {string}
 * @returns {Object.<string, string>}
 */
export const getURLParams = (str) => {
    const params = new URLSearchParams(str);

    return reduce(
        (memo, key) => ({
            ...memo,
            [key]: params.getAll(key).toString()
        }),
        {},
        params.keys()
    );
};

export const getURLParam = (str, param) => getURLParams(str)[param];

export const getLocationParams = () => getURLParams(window.location.search);

export const getLocationParam = param => getURLParam(window.location.search, param);

export const isParamPresent = (param) => {
    const params = new URLSearchParams(window.location.search);

    return params.get(param) !== null;
};

export const getBaseDomain = ({ dotStart = true } = {}) => {
    if (window.Playtech?.Variables?.baseDocumentDomain) {
        return `${dotStart ? '.' : ''}${window.Playtech.Variables.baseDocumentDomain}`;
    }
    // check for IP
    if (/^\d+(\.\d+)*$/.test(document.domain)) {
        return document.domain;
    }

    const domain = document.domain.split('.');

    if (domain.length > 2) {
        const baseDomain = domain.splice(-2).join('.');

        if (dotStart) {
            return `.${baseDomain}`;
        }

        return baseDomain;
    }

    return document.domain;
};

/**
 * @private
 * @typedef {Object} urlPathConfig
 * @property {string|null} lang - Language code if present in passed path and in allowed languages. Example: "en_US", "en_GB", "de_DE"
 * @property {string} friendlyUrl - url path part without language and leading/trailing slashes. Suitable for concatenating in URL. Example: "home", "bonuses/12345"
 * @property {string} cleanUrl - Page's path without language and with leading slash. Example: "/home", "/bonuses/12345"
 * @param path {string} pathname
 * @returns {urlPathConfig}
 */
export const parseUrl = (path = '') => {
    const urlParts = path.split('/').filter(Boolean);
    // Lang always at the head of path
    const lang = languageService.getLanguage(urlParts[0]);
    if (lang) {
        // remove language from url if it's actually a language
        urlParts.shift();
    }
    const friendlyUrl = urlParts.join('/');
    const cleanUrl = `/${friendlyUrl}`;

    return {
        lang,
        friendlyUrl,
        cleanUrl
    };
};

/**
 * Get clean url without leading slash
 * @param {string} path
 * @returns {string}
 */
export const getFriendlyUrl = (path = location.pathname) => parseUrl(path).friendlyUrl;

/**
 * Get url without language
 * @param {string} path
 * @returns {string}
 */
export const getCleanUrl = (path = location.pathname) => parseUrl(path).cleanUrl;

/**
 * Get path without language, slash and default path
 * @param {string} path
 * @returns {string}
 */
export const getCleanPath = (path) => {
    if (!path) {
        return '';
    }

    return parseUrl(path).cleanUrl;
};

/**
 * Get lang from url
 * @param {string} path
 * @returns {string}
 */
export const getLang = (path = location.pathname) => parseUrl(path).lang;

/**
 * Serialize object to search string
 * https://www.w3.org/TR/2012/WD-url-20120524/#url-parameter-serialization
 * @param {Object|Array} obj
 * @param {boolean} [isEncode=true]
 * @returns {string}
 */
export const serialize = (obj, isEncode = true) => Object.keys(obj)
    .map((key) => {
        const k = isEncode ? encodeURIComponent(key) : key;
        const v = isEncode ? encodeURIComponent(obj[key]) : obj[key];

        // check on `null`, `undefined`, `NaN`
        // eslint-disable-next-line no-self-compare
        if (obj[key] === null || obj[key] === undefined || obj[key] !== obj[key]) {
            return k;
        }

        return `${k}=${v}`;
    })
    .join('&');

export const redirect = (url, isReplace) => {
    return isReplace
        ? window.location.replace(url)
        : window.location.href = url;
};

/**
 * Check is url for external page
 * @param  {string | Object}  path
 * @returns {boolean}
 */
export const isExternalUrl = (path) => {
    const [, protocol, host] = getPartsUrl(path);

    if (typeof protocol === 'string' && protocol.length > 0 && protocol.toLowerCase() !== location.protocol) {
        return true;
    }

    return typeof host === 'string'
        && host.length > 0
        && host.replace(new RegExp(`:(${location.protocol === 'https' ? 443 : 80})?$`), '') !== location.host;
};

/**
 * Returned array will contain
 * [0] Full URL
 * [1] Protocol part. Examples: "https:", "http:", "wss:"
 * [2] Domain part. Example: "core3-qa09.mws.playtechgaming.com"
 * [3] Pathname part. Example: "/home"
 * [4] Search part. Example: "?param1=1&param2=0"
 * [5] Hash part. Example: "#abc"
 * @param path {string | Location}
 * @returns {string[]}
 */
export const getPartsUrl = (path) => {
    const url = typeof path === 'string' ? path : path?.pathname || '';

    return url.match(/^([^:/?#]+:)?(?:\/\/([^/?#]*))?([^?#]+)?(\?[^#]*)?(#.*)?/);
};

export const isHashLink = (url) => {
    return url.startsWith('#');
};

export const isJavascriptLink = (url) => {
    // eslint-disable-next-line no-script-url
    return url.toLowerCase().startsWith('javascript:');
};

export const getCombinedSearch = (toUrlSearch = '', fromUrlSearch = '', keysToRemove = []) => {
    const combinedSearch = new URLSearchParams(toUrlSearch);
    const initialSearch = new URLSearchParams(fromUrlSearch);

    for (const key of new Set(initialSearch.keys())) {
        if (!combinedSearch.has(key)) {
            const values = initialSearch.getAll(key);

            for (const value of values) {
                combinedSearch.append(key, value);
            }
        }
    }

    if (keysToRemove.length) {
        return getSearchWithoutParameters(keysToRemove, `?${combinedSearch.toString()}`);
    }

    return `?${combinedSearch.toString()}`;
};

/**
 * Wrapper for window.open(). Preferred over native call. Carries same signature as native.
 * @param {string} [url]
 * @param {string} [name] defaults to '' to override native forced String() conversion of null values
 * @param {string} [features]
 * @param {Function} [handlePopupBlocker]
 * @returns {Window}
 */
export const openWindow = (url, name, features = '', handlePopupBlocker) => {
    const newWindow = window.open(url, name ?? '', features);

    if (!newWindow) {
        handlePopupBlocker?.();
    }

    return newWindow;
};

/**
 * Wrapper for window.open() that open new window by default.
 * @param {string} [url]
 * @param {string} [name]
 * @param {string} [features]
 * @param {Function} [handlePopupBlocker]
 * @returns {*}
 */
export const openNewWindow = (url, name, features = 'resizable=yes, width=800px, height=600px', handlePopupBlocker) => {
    return openWindow(url, name, features, handlePopupBlocker);
};

/**
 * For preventing XSS attack. Returns true if url component starts with javascript: and false otherwise
 * @param {string} uri
 * @returns {boolean}
 */
export const isJavascriptURI = (uri) => {
    const regEx = new RegExp('^' +
        '[\\x00-\\x20\\x7F]*j' +
        '[\\x00-\\x20\\x7F]*a' +
        '[\\x00-\\x20\\x7F]*v' +
        '[\\x00-\\x20\\x7F]*a' +
        '[\\x00-\\x20\\x7F]*s' +
        '[\\x00-\\x20\\x7F]*c' +
        '[\\x00-\\x20\\x7F]*r' +
        '[\\x00-\\x20\\x7F]*i' +
        '[\\x00-\\x20\\x7F]*p' +
        '[\\x00-\\x20\\x7F]*t' +
        '[\\x00-\\x20\\x7F]*:', 'i'
    );

    return regEx.test(uri);
};

export const isDocumentsAndMediaUrl = (url) => {
    if (isExternalUrl(url)) {
        return false;
    }

    const [, , , formattedPath] = getPartsUrl(url);
    const cleanUrl = getCleanUrl(formattedPath);

    return /^\/library\//.test(cleanUrl);
};

export const getPathWithParams = (url) => {
    const [, , , formattedPath = '/', params = ''] = getPartsUrl(url);

    return formattedPath + params;
};

export const getUrlWithTrailingSlash = (url = '', useTrailingSlash) => {
    const [path, searchAndHash] = url.trim().split('?');
    let pathWithTrailingSlash = path;

    if (useTrailingSlash && path.slice(-1) !== '/') {
        pathWithTrailingSlash = `${path}/`;
    } else if (!useTrailingSlash && path.slice(-1) === '/') {
        pathWithTrailingSlash = path.slice(0, -1);
    }

    return [pathWithTrailingSlash, searchAndHash].filter(Boolean).join('?');
};

export const getPathWithTrailingSlash = (path = '', useTrailingSlash) => {
    const trimmedPath = path.trim();

    if (trimmedPath === '/') {
        return trimmedPath;
    }

    return getUrlWithTrailingSlash(trimmedPath, useTrailingSlash);
};

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export const isLaunchGamePath = (pathname) => {
    const gameRegExp = /igaming\//g;

    return gameRegExp.test(pathname);
};

/**
 * It is really important to add query string parameters here
 * because in hub2-shared environment we must pass parameters to all local assets
 * @param src {string}
 * @returns {string}
 */
export const addAllowedParamsToSrc = (src) => {
    try {
        const search = new URLSearchParams(location.search);
        const casinoname = search.get('casinoname');
        const site = search.get('site');
        const allowedParams = {
            ...casinoname && { casinoname },
            ...site && { site }
        };

        return addParamsToUrl(allowedParams, src);
    } catch (error) {
        log('error', error?.message);

        return '';
    }
};
